import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiList } from "react-icons/fi";
import bankService from "../../../services/bank.service";
import { formatVehicleNumberDisplay } from "../../../utils/vehicleNumberUtils";
import PaginationBar from "../../../components/common/PaginationBar";
import { PAGE_LIMIT } from "../../../constants/pagination";
import { readListItems, readPagination } from "../../../utils/listPagination";
import "../../../styles/bankDetails.css";
import "../../../styles/users.css";
import "../../../styles/confirmation.css";
import "../../../styles/bankTraceCases.css";

export default function BankTraceCases({ seizedOnly = false }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");

  useEffect(() => {
    setPage(1);
  }, [appliedQuery]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await bankService.getTraceCases({
        page,
        limit: PAGE_LIMIT,
        search: appliedQuery.trim() || undefined,
        ...(seizedOnly ? { view: "seized" } : {}),
      });
      const body = res?.data;
      setRows(readListItems(body, ["items", "records"]));
      setPagination(readPagination(body));
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load trace cases.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, appliedQuery, seizedOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const openCase = (id) =>
    navigate(seizedOnly ? `/bank/seized-vehicles/${id}` : `/bank/trace-cases/${id}`);

  return (
    <div className="bd-page cases-page cf-page bank-trace-page">
      <div className="users-actions bank-trace-page__head">
        <h2>
          <FiList aria-hidden />
          {seizedOnly ? "Seized vehicles" : "Trace Cases"}
        </h2>
      </div>
      <p className="muted bank-trace-page__hint">
        {seizedOnly
          ? "Vehicles your bank / NBFC uploaded that a linked agency has seized. Open a row to see full details, photos, videos and files."
          : "Vehicles traced from numbers uploaded by your bank / NBFC."}
      </p>

      <form
        className="cf-list-search bank-trace-page__search"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setAppliedQuery(search.trim());
        }}
      >
        <label className="cf-list-search__field bank-trace-page__search-field">
          <span className="sr-only">Search trace cases</span>
          <input
            type="search"
            placeholder="Search vehicle number, loan number or customer name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
        </label>
        <div className="bank-trace-page__search-actions">
          <button type="submit" className="primary-page-btn cf-list-search__btn">
            Search
          </button>
          <button
            type="button"
            className="secondary-page-btn cf-list-search__btn"
            onClick={() => {
              setSearch("");
              setAppliedQuery("");
              setPage(1);
            }}
          >
            Reset
          </button>
        </div>
      </form>

      {error ? <p className="bd-feedback bd-error">{error}</p> : null}

      {loading ? (
        <p className="cf-excel-loading">Loading cases…</p>
      ) : rows.length === 0 && !error ? (
        <p className="cf-excel-loading">
          {appliedQuery.trim()
            ? `No cases match “${appliedQuery.trim()}”.`
            : seizedOnly
              ? "No seized vehicles yet. When a linked agency seizes a vehicle you uploaded, it will appear here."
              : "No matching trace cases yet. When a linked agency traces a vehicle you uploaded, it will appear here."}
        </p>
      ) : (
        <>
          <div className="company-table-wrap bank-trace-page__table">
            <table className="users-table excel-grid-table cf-excel-list-table">
              <thead>
                <tr>
                  <th>Vehicle Number</th>
                  <th>Loan Number</th>
                  <th>Customer Name</th>
                  <th>Trace By</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row._id}
                    className="confirmation-row-clickable"
                    onClick={() => openCase(row._id)}
                  >
                    <td>{formatVehicleNumberDisplay(row.vehicleNumber) || "—"}</td>
                    <td>{row.loanNumber || "—"}</td>
                    <td>{row.customerName || "—"}</td>
                    <td>{row.traceBy || "—"}</td>
                    <td>
                      <span className="cf-badge cf-badge--confirmed">{row.status || "Traced"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="bank-trace-page__cards">
            {rows.map((row) => (
              <li key={`card-${row._id}`}>
                <button
                  type="button"
                  className="bank-trace-card"
                  onClick={() => openCase(row._id)}
                >
                  <div className="bank-trace-card__top">
                    <strong>{formatVehicleNumberDisplay(row.vehicleNumber) || "—"}</strong>
                    <span className="cf-badge cf-badge--confirmed">{row.status || "Traced"}</span>
                  </div>
                  <p>
                    <span>Customer</span>
                    {row.customerName || "—"}
                  </p>
                  <p>
                    <span>Loan Number</span>
                    {row.loanNumber || "—"}
                  </p>
                  <p>
                    <span>Trace By</span>
                    {row.traceBy || "—"}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {!loading ? (
        <PaginationBar
          page={pagination.page || page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          loading={loading}
          onPageChange={setPage}
        />
      ) : null}
    </div>
  );
}
